import React, { useEffect, useState } from 'react'
import { addProductRecommendation, deleteUserProductRecommendation, generateUserSignature, getUserProductRecommendations, getUserSignature } from '../../../../services/users'
import { Badge, Box, Button, CopyButton, Flex, Group, Image, Loader, Modal, Pagination, Paper, Table, Text, ThemeIcon, Tooltip } from '@mantine/core'
import { IconCheck, IconCopy, IconWindowMaximize } from '@tabler/icons-react'
import LoadingData from '../../../ui/LoadingData'
import NoData from '../../../ui/NoData'
import NoImage from '../../../../assets/img/images/no-images.png'
import { notificationError, notificationSuccess } from '../../../ui/Notifications'
import InputProductActionBanner from '../../Banner/InputProductActionBanner'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'

const labelTable = [
  {
    label: 'Id Product',
    width: 100
  },
  {
    label: 'Image',
    width: 100
  },
  {
    label: 'By User',
    width: 200
  },
  {
    label: 'Product Name',
    width: 'auto'
  },
  {
    label: 'Response',
    width: 150
  },
  {
    label: 'Action',
    width: 150
  },
]

const responseStatus = ['No Response', 'Interested', 'Not Interested']

const UserRecommendations = () => {
  const params = useParams()
  const userId = params.id

  const apiUrl = process.env.REACT_APP_SERVER_API_URL

  // signature user recommendation url
  const [signature, setSignature] = useState(null)
  const [loadingSignature, setLoadingSignature] = useState(true)
  const [loadingGenerateSignature, setLoadingGenerateSignature] = useState(false)

  const [loading, setLoading] = useState(true)
  const [recommendationProducts, setRecommendationProducts] = useState([])
  const [totalData, setTotalData] = useState(0)
  const [pageNumber, setPageNumber] = useState(1)

  // add product recommendation
  const [formProductIds, setFormProductIds] = useState([])
  const [loadingAddProduct, setLoadingAddProduct] = useState(false)
  const [openModalProduct, setOpenModalProduct] = useState(false)

  const onCloseModal = () => {
    setOpenModalProduct(false)
    setFormProductIds([])
  }

  const getProductRecommendations = async (userId) => {
    setLoading(true)
    const variables = {
      first: 10,
      page: pageNumber,
      where: {
        column: 'USER',
        value: userId
      },
      orderBy: [
        {
          column: 'CREATED_AT',
          order: 'DESC'
        }
      ]
    }

    try {
      const response = await getUserProductRecommendations(variables)

      const dataRecommend = response.data.data.productRecommendations.data
      const paginatorInfo = response.data.data.productRecommendations.paginatorInfo

      const remap = dataRecommend.map((val) => {
        return {
          ...val.product,
          agentId: Number(val.agentId),
          creator: val.agent,
          response: val.response,
          userId: Number(val.userId),
          createdAt: val.createdAt
        }
      })
      setRecommendationProducts(remap)
      setTotalData(paginatorInfo.total)
    } catch (error) {
      notificationError('Something Went Wrong', error.message)
    } finally {
      setLoading(false)
    }
  }

  // add product recommendation
  const handleAddProductRecommendation = async () => {
    setLoadingAddProduct(true)
    try {
      const payload = {
        products: [
          ...formProductIds,
        ]
      }
      const response = await addProductRecommendation(userId, payload)
      if (response.success) {
        notificationSuccess('Add Product Recommendation Success', response.message)

        getProductRecommendations(userId)
        onCloseModal()
      } else {
        const message = response.message === 'Products not been recommended to User'
          ? 'This products has been added as a recommended product by this user before.'
          : response.message
        notificationError('Cannot add Product Recommendation to User', message)
      }
    } catch (error) {
      notificationError('Something Went wrong', error.message)
    } finally {
      setLoadingAddProduct(false)
    }
  }

  // delete product recommendation
  const handleDeleteProduct = async (productId) => {
    try {
      const response = await deleteUserProductRecommendation(userId, productId)
      if (response.success) {
        notificationSuccess('Delete Success', response.message)
      }
    } catch (error) {
      notificationError('Something Went wrong', error.message)
    } finally {
      getProductRecommendations(userId)
    }

  }

  // user signature
  const handleGetUserSignature = async (userId) => {
    setLoadingSignature(true)
    try {
      const response = await getUserSignature(userId)
      const signature = response.data.data.user.signature
      setSignature(signature)
    } catch (error) {
      notificationError('Something Went wrong', error.message)
    } finally {
      setLoadingSignature(false)
    }
  }

  const handleGenerateSignature = async (userId) => {
    setLoadingGenerateSignature(true)
    try {
      const response = await generateUserSignature(userId)
      const signature = response.data.data.productRecommendationPublicURLCreate.signature
      setSignature(signature)
      notificationSuccess('Generate Signature Success', 'The recommendation URL signature has been generated successfully.')
    } catch (error) {
      notificationError('Something Went wrong', error.message)
    } finally {
      setLoadingGenerateSignature(false)
    }
  }

  useEffect(() => {
    if (userId) {
      getProductRecommendations(userId)
      handleGetUserSignature(userId)
    }
    // eslint-disable-next-line
  }, [userId, pageNumber])

  const handleApiUrl = (api) => {
    if (api.includes('api2dev')) {
      return 'https://app-dev.asiacommerce.net'
    } else {
      return 'https://app.asiacommerce.net'
    }
  }

  const openNewTab = (url) => {
    window.open(`${url}`, '_blank')
  }

  const renderTableProduct = (dataProduct) => {
    const mappingTableHead = (data) => data.map((val, index) => {
      return (<Table.Th key={index} width={val.width !== 'auto' ? val.width : ''}>{val.label}</Table.Th>)
    })
    const mappingDataTable = (data) => data.map((val, index) => {
      return (
        <Table.Tr key={index}>
          <Table.Td>
            <Flex gap={2} align='center'>
              <Text fz={14} c='blue.9'>{val.id}</Text>
              <Tooltip label='Open Link' withArrow position="top">
                <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`/product/${val.id}`)}>
                  <IconWindowMaximize size={12} />
                </ThemeIcon>
              </Tooltip>
            </Flex>
          </Table.Td>
          <Table.Td>
            <Paper shadow="sm" w={62} withBorder radius='md'  style={{ cursor: 'pointer' }}>
              <Image w={60} h={60} radius='md' src={val.images[0]?.source ? val.images[0]?.source : NoImage} />
            </Paper>
          </Table.Td>
          <Table.Td>
            <Box>
              <Flex gap={2} align='center'>
                <Text fz={14} c='blue.9'>{val.creator.name}</Text>
                <Tooltip label='Open Link' withArrow position="top">
                  <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`/users/${val.creator.id}`)}>
                    <IconWindowMaximize size={12} />
                  </ThemeIcon>
                </Tooltip>
              </Flex>
              <Flex gap={2} align='center'>
                <Text fz={12}>ID:</Text>
                <Text fz={12} c='blue.9'>{val.creator.id}</Text>
              </Flex>

              <Text fz={12} color='gray'>{val.createdAt}</Text>
            </Box>
          </Table.Td>
          <Table.Td>{val.title}</Table.Td>
          <Table.Td>
            <Badge variant="light" color="gray">{responseStatus[val.response]}</Badge>
          </Table.Td>
          <Table.Td>
            <Flex gap={'sm'}>
              <Button
                variant="filled"
                size="xs"
                onClick={() => openNewTab(`${handleApiUrl(apiUrl)}/recommendation/public/${userId}${val.agentId ? `?agent=${val.agentId}` : ''}`)}
              >
                Detail
              </Button>
              <Button variant="light" size="xs" onClick={() => handleDeleteProduct(val.id)}>Delete</Button>
            </Flex>
          </Table.Td>
        </Table.Tr>
      )
    })
    
    return (
      <Box>
        <Table.ScrollContainer mt={25} minWidth={1200} type='native'>
          <Table highlightOnHover withTableBorder style={{ fontSize: '13px' }}>
            <Table.Thead style={{ backgroundColor: '#eeefef' }}>
              <Table.Tr>
                {mappingTableHead(labelTable)}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody style={{ verticalAlign: 'top' }}>
              {mappingDataTable(dataProduct)}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
        <Flex justify='end' mt={10}>
          <Pagination size='sm' onChange={(val) => setPageNumber(val)} value={pageNumber} total={totalData > 0 ? Math.ceil(totalData / 10) : totalData} />
        </Flex>
      </Box>
    )
  }

  const renderUrlRecommendation = (signature) => {
    if (signature) {
      return (
        <Flex gap={4} align='center'>
          <Text fz={14}>Personal Recommendation URL:</Text>
          <Text fz={14} c='blue.9'>{`${handleApiUrl(apiUrl)}/recommendation/${signature}`}</Text>
          <CopyButton value={`${handleApiUrl(apiUrl)}/recommendation/${signature}`} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ThemeIcon>
              </Tooltip>
            )}
          </CopyButton>
          <Tooltip label='Open Link' withArrow position="top">
            <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${handleApiUrl(apiUrl)}/recommendation/${signature}`)}>
              <IconWindowMaximize size={12} />
            </ThemeIcon>
          </Tooltip>
        </Flex>
      )

    }
    else {
      return (
        <Flex gap={4} align='center'>
          <Text fz={14}>Personal Recommendation URL:</Text>
          <Button
            loading={loadingSignature || loadingGenerateSignature}
            variant="transparent"
            color='blue'
            size="compact-xs"
            radius="xl"
            onClick={() => handleGenerateSignature(userId)}
          >
              Click to Generate Url
            </Button>
        </Flex>
      )
    }
  }

  const renderUrlRecommendationByAgent = (signature) => {
    if (signature) {
      return (
        <Flex gap={4} align='center'>
          <Text fz={14}>Personal Recommendation URL by Agent:</Text>
          <Text fz={14} c='blue.9'>{`${handleApiUrl(apiUrl)}/recommendation/${signature}?agent=IDUSERAGENT`}</Text>
          <CopyButton value={`${handleApiUrl(apiUrl)}/recommendation/${signature}?agent=IDUSERAGENT`} timeout={2000}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="top">
                <ThemeIcon color={copied ? 'teal' : 'blue.9'} variant="subtle" onClick={copy} size='xs'>
                  {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
                </ThemeIcon>
              </Tooltip>
            )}
          </CopyButton>
          <Tooltip label='Open Link' withArrow position="top">
            <ThemeIcon color='blue.9' variant="subtle" size='xs' onClick={() => openNewTab(`${handleApiUrl(apiUrl)}/recommendation/${signature}?agent=IDUSERAGENT`)}>
              <IconWindowMaximize size={12} />
            </ThemeIcon>
          </Tooltip>
        </Flex>
      )
    }
  }

  return (
    <Box my={20} mx={10}>
      <Group justify="space-between">
        <Text fz={20} fw={500} tt="capitalize">Recommendations</Text>
        <Button size='sm' onClick={() => setOpenModalProduct(true)}>
          + Add Recommendation
        </Button>
      </Group>

      <Box>
        <Text fz={14}>Product Recomendation Total: {totalData}</Text>

        {renderUrlRecommendation(signature)}

        {renderUrlRecommendationByAgent(signature)}
      </Box>

      {/** Table */}
      {loading ? <LoadingData /> : recommendationProducts.length !== 0 ? renderTableProduct(recommendationProducts) : <NoData />}

      <Modal opened={openModalProduct} onClose={() => onCloseModal()} centered size='xl' title={<Text fw='Bold'>Set Product Recommendations</Text>}  zIndex={200}>
        
        <InputProductActionBanner
          dataProductIds={formProductIds}
          onChange={(val) => setFormProductIds(val)}
          isError={false}
        />

        <Flex mt='md' gap={'md'} justify={'end'}>
          <Button variant='outline' onClick={() => onCloseModal()}>Cancel</Button>
          <Button loading={loadingAddProduct} disabled={formProductIds.length === 0} onClick={() => handleAddProductRecommendation()} >Submit</Button>
        </Flex>
      </Modal>
    </Box>
  )
}

export default UserRecommendations